import { ref } from "vue";

const MD_BREAKPOINT = 768;
const LG_BREAKPOINT = 1024;

const PINNED_KEY = "sidenav-pinned";

const isSmallScreen = ref(typeof window !== "undefined" && window.innerWidth < MD_BREAKPOINT);
const isMediumScreen = ref(
  typeof window !== "undefined" &&
    window.innerWidth >= MD_BREAKPOINT &&
    window.innerWidth < LG_BREAKPOINT,
);
const isSmallScreenDrawerOpen = ref(false);
const isHoverExpanded = ref(false);
function safeGetPinned(): boolean {
  try {
    return typeof window !== "undefined" && localStorage.getItem(PINNED_KEY) === "true";
  } catch {
    // Storage blocked (e.g. privacy mode): default to unpinned.
    return false;
  }
}

const isPinned = ref(safeGetPinned());

export function useSideNav() {
  let smQuery: MediaQueryList | null = null;
  let lgQuery: MediaQueryList | null = null;

  function initSideNav() {
    smQuery = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
    lgQuery = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);

    isSmallScreen.value = smQuery.matches;
    isMediumScreen.value = !smQuery.matches && !lgQuery.matches;

    smQuery.addEventListener("change", onSmChange);
    lgQuery.addEventListener("change", onLgChange);
  }

  function onSmChange(e: MediaQueryListEvent) {
    isSmallScreen.value = e.matches;
    isMediumScreen.value = !e.matches && !(lgQuery?.matches ?? false);
    if (!e.matches) closeSmallScreenDrawer();
    isHoverExpanded.value = false;
  }

  function onLgChange(e: MediaQueryListEvent) {
    isMediumScreen.value = !e.matches && !(smQuery?.matches ?? false);
    isHoverExpanded.value = false;
  }

  function toggleSmallScreenDrawer() {
    isSmallScreenDrawerOpen.value = !isSmallScreenDrawerOpen.value;
  }

  function closeSmallScreenDrawer() {
    isSmallScreenDrawerOpen.value = false;
  }

  function togglePin() {
    isPinned.value = !isPinned.value;

    try {
      localStorage.setItem(PINNED_KEY, String(isPinned.value));
    } catch {
      // Storage disabled or quota exceeded: keep the in-memory state, it
      // just won't survive a reload.
    }

    if (isPinned.value) {
      isHoverExpanded.value = false;
    }
  }

  function onMouseEnter() {
    if (!isSmallScreen.value && !isPinned.value) isHoverExpanded.value = true;
  }

  function onMouseLeave() {
    if (!isSmallScreen.value && !isPinned.value) isHoverExpanded.value = false;
  }

  return {
    isSmallScreen,
    isMediumScreen,
    isSmallScreenDrawerOpen,
    isHoverExpanded,
    isPinned,
    initSideNav,
    toggleSmallScreenDrawer,
    closeSmallScreenDrawer,
    togglePin,
    onMouseEnter,
    onMouseLeave,
  };
}
